import { useState } from 'react'
import { useAuth } from '../lib/auth.jsx'
import AccountMenu from './AccountMenu.jsx'
import * as navPersist from '../lib/navPersist.js'
import { isActive } from '../lib/routes.js'
import { House, CheckSquare, AddressBook, Buildings, ClipboardText, Briefcase, Microphone, Newspaper, UsersThree, Wrench, BookOpen, Moon, Sun, CaretDown, CaretRight } from '@phosphor-icons/react'

/**
 * Which sidebar accordions are open. `projects` is the only expandable section —
 * Speaking and PR & Media became flat destinations on 2026-07-28 when their
 * per-engagement preview lists were removed.
 *
 * `??` rather than `||`: a stored `false` is a deliberate collapse by the user
 * and must win over the route-derived default.
 */
export function resolveOpenSections(route, stored = {}) {
  return { projects: stored.projects ?? (route?.type === 'project') }
}

// Persistent left navigation (Asana/ClickUp/Todoist convention):
// My Tasks destination + project list + account/settings footer.
export default function Sidebar({
  route,
  projects,
  myCount,
  advisoryEngagements = [],
  speakingEngagements = [],
  prEngagements = [],
  // Unused here since the per-engagement preview lists were removed — kept in
  // the signature deliberately so Taskboard.jsx doesn't need an edit. Do not
  // delete or try to "wire it up".
  onOpenEngagement,
  onGoHome,
  onGoMyTasks,
  onGoTeam,
  onGoTools,
  onGoPlaybook,
  onGoSpeaking,
  onGoPR,
  onGoContacts,
  onGoCompanies,
  onGoAdvisory,
  onGoForms,
  onSelectProject,
  onNewProject,
  onOpenSettings,
  theme,
  onToggleTheme,
  onCloseMobile,
  updateProfile,
  signOut,
}) {
  const { user } = useAuth()
  const [openSections, setOpenSections] = useState(() =>
    resolveOpenSections(route, navPersist.getOpenSections()),
  )
  if (!user) return null
  const isDark = theme !== 'light'

  const setSection = (k, open) => {
    setOpenSections((s) => ({ ...s, [k]: open }))
    navPersist.setOpenSection(k, open)
  }
  const openSection = (k) => setSection(k, true)
  // toggle derives from previous state (not a stale closure). setOpenSection is
  // idempotent, so persisting inside the updater is safe under StrictMode re-invocation.
  const toggle = (k) =>
    setOpenSections((s) => {
      const next = !s[k]
      navPersist.setOpenSection(k, next)
      return { ...s, [k]: next }
    })

  const navItem = (active) => ({
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    width: '100%',
    textAlign: 'left',
    background: active ? 'rgba(200,168,75,.10)' : 'none',
    border: 'none',
    borderLeft: '2px solid ' + (active ? 'var(--color-gold)' : 'transparent'),
    borderRadius: 0,
    padding: '9px 14px',
    fontSize: 13.5,
    fontWeight: active ? 600 : 500,
    color: active ? 'var(--color-white)' : 'var(--text-body)',
    cursor: 'pointer',
  })

  // Accordion section header: chevron toggles collapse, label triggers
  // navigation (and ensures the section is open), optional trailing count
  // and an optional extra action button (e.g. Projects' "+").
  const sectionHeader = (key, label, count, { onLabelClick, active, extra } = {}) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '0 14px' }}>
      <button
        onClick={(e) => { e.stopPropagation(); toggle(key) }}
        aria-label={openSections[key] ? `Collapse ${label}` : `Expand ${label}`}
        style={{ background: 'none', border: 'none', color: 'var(--color-muted)', cursor: 'pointer', padding: 0, width: 12, display: 'flex', alignItems: 'center', flexShrink: 0 }}
      >
        {openSections[key] ? <CaretDown size={12} weight="bold" /> : <CaretRight size={12} weight="bold" />}
      </button>
      <button
        onClick={() => { if (onLabelClick) onLabelClick(); else toggle(key) }}
        style={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0, background: 'none', border: 'none', padding: '7px 0', cursor: 'pointer', textAlign: 'left', color: active ? 'var(--color-white)' : 'var(--color-muted)' }}
      >
        <span className="tag-label" style={{ color: 'inherit' }}>{label}</span>
      </button>
      <span className="font-mono" style={{ fontSize: 11, color: 'var(--color-muted)' }}>{count}</span>
      {extra}
    </div>
  )

  return (
    <>
        {/* brand */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '16px 16px 14px' }}>
          <img src="/assets/mark.svg" alt="Career Nomad" style={{ width: 24, height: 24, display: 'block' }} />
          <span className="font-display" style={{ fontSize: 24, letterSpacing: '.03em', textTransform: 'uppercase', color: 'var(--color-white)', lineHeight: 1 }}>
            Caravan
          </span>
          <div style={{ flex: 1 }} />
          <button className="cn-hamburger" onClick={onCloseMobile} title="Close menu" style={{ background: 'none', border: 'none', color: 'var(--color-muted)', cursor: 'pointer', fontSize: 18, lineHeight: 1 }}>✕</button>
        </div>

        {/* Personal tier */}
        <div style={{ padding: '18px 14px 6px' }}>
          <span className="tag-label" style={{ color: 'var(--color-muted)' }}>Personal</span>
        </div>
        <nav style={{ marginTop: 4 }}>
          <button onClick={() => { onGoHome(); onCloseMobile() }} style={navItem(isActive('home', route))}>
            <House size={16} weight={isActive('home', route) ? 'fill' : 'regular'} />
            <span style={{ flex: 1 }}>Home</span>
          </button>
          <button onClick={() => { onGoMyTasks(); onCloseMobile() }} style={navItem(isActive('mytasks', route))}>
            <CheckSquare size={16} weight={isActive('mytasks', route) ? 'fill' : 'regular'} />
            <span style={{ flex: 1 }}>My Tasks</span>
            {myCount > 0 && (
              <span className="font-mono" style={{ fontSize: 11, color: isActive('mytasks', route) ? 'var(--color-gold)' : 'var(--color-muted)' }}>{myCount}</span>
            )}
          </button>
        </nav>

        {/* Workspace tier */}
        <div style={{ padding: '18px 14px 6px' }}>
          <span className="tag-label" style={{ color: 'var(--color-muted)' }}>Workspace</span>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
          {/* darker Workspace panel: Relationships + accordions grouped together */}
          <div style={{ background: 'rgba(0,0,0,.16)', borderRadius: 'var(--radius-md)', margin: '0 8px 4px', padding: '6px 0' }}>
            {/* relationships */}
            <div style={{ padding: '6px 0' }}>
              {/* Wave 2: Contacts is the front door (Kaycia questioned whether
                  Contacts AND Companies were both needed). Companies is kept —
                  it carries the dedup guards, association hops and rollups — but
                  demoted to a secondary row beneath Contacts rather than reading
                  as its own peer destination. Deals is gone: folded into
                  Engagements below. */}
              <div className="tag-label" style={{ padding: '0 14px 6px', color: 'var(--color-muted)' }}>Relationships</div>
              <button onClick={() => { onGoContacts(); onCloseMobile() }} style={navItem(isActive('contacts', route))}>
                <AddressBook size={16} weight={isActive('contacts', route) ? 'fill' : 'regular'} />
                <span style={{ flex: 1 }}>Contacts</span>
              </button>
              <button
                onClick={() => { onGoCompanies(); onCloseMobile() }}
                style={{ ...navItem(isActive('companies', route)), paddingLeft: 28, fontSize: 12.5 }}
              >
                <Buildings size={16} weight={isActive('companies', route) ? 'fill' : 'regular'} />
                <span style={{ flex: 1 }}>Companies</span>
              </button>
              <button onClick={() => { onGoForms(); onCloseMobile() }} style={navItem(isActive('forms', route))}>
                <ClipboardText size={16} weight={isActive('forms', route) ? 'fill' : 'regular'} />
                <span style={{ flex: 1 }}>Forms</span>
              </button>
            </div>

            {/* projects accordion */}
            <div style={{ padding: '4px 0' }}>
              {sectionHeader('projects', 'Projects', projects.length, {
                onLabelClick: () => {
                  const target = route.type === 'project' ? route.projectId : projects[0]?.id
                  if (target) onSelectProject(target)
                  openSection('projects')
                  onCloseMobile()
                },
                active: route.type === 'project',
                extra: (
                  <button onClick={(e) => { e.stopPropagation(); onNewProject() }} title="New project" style={{ background: 'none', border: 'none', color: 'var(--color-muted)', cursor: 'pointer', fontSize: 16, lineHeight: 1, padding: 0 }}>+</button>
                ),
              })}
              {openSections.projects && (
                <div>
                  {projects.map((p) => {
                    const active = route.type === 'project' && route.projectId === p.id
                    return (
                      <button key={p.id} onClick={() => { onSelectProject(p.id); onCloseMobile() }} style={navItem(active)}>
                        <span style={{ width: 7, height: 7, borderRadius: 2, background: active ? 'var(--color-gold)' : 'var(--border-dash)', flexShrink: 0 }} />
                        <span style={{ flex: 1, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.name}</span>
                      </button>
                    )
                  })}
                  {projects.length === 0 && (
                    <div style={{ fontSize: 12.5, color: 'var(--text-dim)', padding: '6px 16px', lineHeight: 1.5 }}>No projects yet. Tap + to create one.</div>
                  )}
                </div>
              )}
            </div>

            {/* Engagements: one group over the three types (Kaycia's sidebar
                mockup — "Engagements > Advisory engagements · Speaking · PR and
                media"). Flat destinations: the per-engagement preview lists were
                removed 2026-07-28 because with 48 engagements the expanded list
                made the left panel far taller than the viewport. The boards
                themselves are the place to browse records. */}
            <div style={{ padding: '4px 0' }}>
              <div className="tag-label" style={{ padding: '0 14px 6px', color: 'var(--color-muted)' }}>Engagements</div>
              <button
                onClick={() => { onGoAdvisory(); onCloseMobile() }}
                style={navItem(isActive('advisory', route))}
              >
                <Briefcase size={16} weight={isActive('advisory', route) ? 'fill' : 'regular'} />
            <span style={{ flex: 1 }}>Advisory</span>
                {advisoryEngagements.length > 0 && (
                  <span className="font-mono" style={{ fontSize: 11, color: 'var(--color-muted)' }}>{advisoryEngagements.length}</span>
                )}
              </button>
              <button
                onClick={() => { onGoSpeaking(); onCloseMobile() }}
                style={navItem(isActive('speaking', route))}
              >
                <Microphone size={16} weight={isActive('speaking', route) ? 'fill' : 'regular'} />
            <span style={{ flex: 1 }}>Speaking</span>
                {speakingEngagements.length > 0 && (
                  <span className="font-mono" style={{ fontSize: 11, color: 'var(--color-muted)' }}>{speakingEngagements.length}</span>
                )}
              </button>
              <button
                onClick={() => { onGoPR(); onCloseMobile() }}
                style={navItem(isActive('pr', route))}
              >
                <Newspaper size={16} weight={isActive('pr', route) ? 'fill' : 'regular'} />
            <span style={{ flex: 1 }}>PR &amp; Media</span>
                {prEngagements.length > 0 && (
                  <span className="font-mono" style={{ fontSize: 11, color: 'var(--color-muted)' }}>{prEngagements.length}</span>
                )}
              </button>
            </div>
          </div>

          {/* Hub section (outside the darker Workspace panel) */}
          <div style={{ padding: '12px 14px 6px' }}>
            <span className="tag-label" style={{ color: 'var(--color-muted)' }}>Hub</span>
          </div>
          <nav>
            <button onClick={() => { onGoTeam(); onCloseMobile() }} style={navItem(isActive('team', route))}>
              <UsersThree size={16} weight={isActive('team', route) ? 'fill' : 'regular'} />
            <span style={{ flex: 1 }}>Team</span>
            </button>
            <button onClick={() => { onGoTools(); onCloseMobile() }} style={navItem(isActive('tools', route))}>
              <Wrench size={16} weight={isActive('tools', route) ? 'fill' : 'regular'} />
            <span style={{ flex: 1 }}>Tools &amp; Links</span>
            </button>
            <button onClick={() => { onGoPlaybook(); onCloseMobile() }} style={navItem(isActive('playbook', route))}>
              <BookOpen size={16} weight={isActive('playbook', route) ? 'fill' : 'regular'} />
            <span style={{ flex: 1 }}>Playbook</span>
            </button>
          </nav>
        </div>

        {/* footer */}
        <div style={{ borderTop: '1px solid var(--color-divider-dark)', padding: 8 }}>
          <button onClick={onToggleTheme} style={navItem(false)} title="Toggle theme">
            {isDark ? <Moon size={16} /> : <Sun size={16} />}
            <span>{isDark ? 'Dark theme' : 'Light theme'}</span>
          </button>
          <AccountMenu
            user={user}
            updateProfile={updateProfile}
            signOut={signOut}
            theme={theme}
            onToggleTheme={onToggleTheme}
            onOpenSettings={() => { onOpenSettings(); onCloseMobile() }}
          />
        </div>
    </>
  )
}
